/**
 * Edge Fleet > Agent removal (release 8 item 11, laptop agent deployed once).
 *
 * Two things, both decided by the founder on 17 Oct 2026:
 *   - The emergency MAINTENANCE CODE. One per organisation, made by TWO people:
 *     one person asks to make or rotate it, a DIFFERENT person approves, and the
 *     code is shown ONCE to the approver. From the agents' next check-in, an
 *     agent cannot be uninstalled on a laptop without it or an approved removal.
 *     The console keeps only a salted hash; nobody can show the code again, so
 *     it goes straight into the organisation's password vault.
 *   - REMOVAL REQUESTS. A removal is asked for in the device drawer ("Remove
 *     agent") and approved here by a DIFFERENT person (four eyes, like update
 *     rollouts); the agent then removes itself on its next check-in.
 *
 * Buttons follow the permissions (useCan); the controller decides.
 */
import { useCallback, useEffect, useState } from 'react';
import { Check, Clipboard, KeyRound, Loader2, ShieldCheck, ShieldOff, Trash2, X } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan, useMe } from '@/lib/access';
import {
  approveRemoval,
  cancelRemoval,
  getMaintenanceCodeStatus,
  listRemovalRequests,
  ownRequest,
  progressWords,
  rejectRemoval,
  REMOVE_APPROVE,
  REMOVE_WRITE,
  requestMaintenanceCodeChange,
  approveMaintenanceCodeChange,
  rejectMaintenanceCodeChange,
  cancelMaintenanceCodeChange,
  type MaintenanceCodeRequest,
  type MaintenanceCodeStatus,
  type NewMaintenanceCode,
  type RemovalRequest,
} from '@/lib/agent-removal-api';

function problemText(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '-');

function StateBadge({ r }: { r: RemovalRequest }) {
  const label =
    r.state === 'awaiting_approval'
      ? 'waiting for approval'
      : r.state === 'approved'
        ? progressWords(r.progress)
        : r.state;
  const tone = r.state === 'approved' && r.progress === 'refused_by_device' ? 'destructive' : 'glass';
  return (
    <Badge variant={tone as 'glass'} className="text-[10px]">
      {label}
    </Badge>
  );
}

export function AgentRemovalPanel(): JSX.Element | null {
  const canAsk = useCan(REMOVE_WRITE);
  const canApprove = useCan(REMOVE_APPROVE);
  const { me } = useMe();
  const actorId = me?.actorId ?? null;

  const [status, setStatus] = useState<MaintenanceCodeStatus | null>(null);
  const [requests, setRequests] = useState<RemovalRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [fresh, setFresh] = useState<NewMaintenanceCode | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([getMaintenanceCodeStatus(), listRemovalRequests()]);
      setStatus(s);
      setRequests(r.requests);
      setError(null);
    } catch (e) {
      setError(problemText(e, 'could not load agent removal'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(key: string, run: () => Promise<unknown>): Promise<void> {
    setBusy(key);
    setError(null);
    try {
      await run();
      await load();
    } catch (e) {
      setError(problemText(e, 'the request failed'));
    } finally {
      setBusy(null);
    }
  }

  async function requestCode(): Promise<void> {
    const rotating = status?.configured === true;
    const reason = window.prompt(
      rotating
        ? 'Ask to rotate the maintenance code? A DIFFERENT person must approve, and the new code is shown to them once. Each laptop takes the new code at its next check-in; until then an offline laptop still accepts the OLD code. Enter a short reason (recorded in the audit chain):'
        : 'Ask to make the maintenance code? A DIFFERENT person must approve, and the code is shown to them once. Agents from release 8 installed by the MSI then refuse to be uninstalled on a laptop without it or an approved removal, from their next check-in. Enter a short reason (recorded in the audit chain):',
      rotating ? 'The code holder changed' : 'Break-glass code for agent removal',
    );
    if (!reason || reason.trim().length < 3) return;
    await act('code', () => requestMaintenanceCodeChange(reason.trim()));
  }

  async function approveCode(id: string): Promise<void> {
    await act('code', async () => {
      const out = await approveMaintenanceCodeChange(id);
      setFresh(out);
      setCopied(false);
    });
  }

  async function copyCode(): Promise<void> {
    if (!fresh) return;
    try {
      await navigator.clipboard.writeText(fresh.code);
      setCopied(true);
    } catch {
      // Older browsers without permission: the code is on screen to type into the vault.
    }
  }

  const waiting = requests.filter((r) => r.state === 'awaiting_approval');
  const history = requests.filter((r) => r.state !== 'awaiting_approval').slice(0, 20);

  return (
    <GlassCard className="p-5 mt-4" data-testid="agent-removal-panel">
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" />
          Agent removal
        </CardTitle>
        <CardDescription>
          The endpoint agent is removed from a laptop only when two people agree here, or on the laptop by an
          administrator who has the organisation&apos;s maintenance code. A local administrator can still defeat any
          agent on their own machine; this makes removal deliberate and visible, not impossible.
        </CardDescription>
      </CardHeader>

      {error && (
        <div className="text-xs text-destructive mb-3" role="alert">
          {error}
        </div>
      )}

      <div className="glass-lite rounded-md p-3 mb-4 space-y-2" data-testid="maintenance-code-status">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Maintenance code
          </div>
          {canApprove && !status?.pendingRequest && (
            <Button data-testid="request-maintenance-code" size="sm" variant="glass" disabled={busy !== null} onClick={() => void requestCode()}>
              {busy === 'code' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              {status?.configured ? 'Request a rotation' : 'Request the code'}
            </Button>
          )}
        </div>
        {status === null ? (
          <div className="text-xs text-muted-foreground">Loading...</div>
        ) : status.configured ? (
          <div className="text-xs text-muted-foreground space-y-1">
            <div>
              In force since {when(status.createdAt)}, made by {status.createdBy}.
              {status.retiredCount > 0 ? ` ${status.retiredCount} earlier code(s) retired.` : ''}
            </div>
            <div data-testid="maintenance-code-coverage">
              {status.laptopsOnCurrentCode} of {status.laptopsTotal} laptop agents hold this code and refuse an uninstall
              without it or an approved removal. The others (agents older than release 8, or laptops that have not
              checked in since the code was made or rotated) are not protected by it yet, and an offline laptop still
              accepts the code it last received.
            </div>
          </div>
        ) : (
          <div className="text-xs text-warning flex items-start gap-1.5">
            <ShieldOff className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              No code yet: agents can be uninstalled on a laptop by any administrator. Request the code to switch removal
              protection on.
            </span>
          </div>
        )}
        <div className="text-[11px] text-muted-foreground" data-testid="maintenance-two-person-note">
          Two people are needed: one with access to every business unit requests the code, a different such person
          approves and is the only one shown the code (store it in your vault at once). Console removals need two people
          too, so make sure at least two people have the approver permission before you rely on this.
        </div>
        {status?.pendingRequest && (
          <div className="rounded-md border border-warning/50 p-3 space-y-2 text-xs" data-testid="maintenance-code-pending">
            {(() => {
              const pr = status.pendingRequest;
              const own = ownRequest(pr, actorId);
              return (
                <>
                  <div className="font-medium">
                    {pr.action === 'rotate' ? 'A rotation of the code is waiting for a second person.' : 'A new code is waiting for a second person.'}
                  </div>
                  <div className="text-muted-foreground">
                    Asked by {pr.requestedBy} on {when(pr.requestedAt)}; expires {when(pr.expiresAt)}. Reason: {pr.reason}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    {canApprove && (
                      <Button
                        data-testid="approve-maintenance-code"
                        size="sm"
                        variant="glass"
                        disabled={busy !== null || own}
                        title={own ? 'Four eyes: you asked for it; a different approver approves and sees the code' : undefined}
                        onClick={() => void approveCode(pr.id)}
                      >
                        {busy === 'code' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                        Approve and reveal the code
                      </Button>
                    )}
                    {canApprove && !own && (
                      <Button
                        size="sm"
                        variant="glass"
                        disabled={busy !== null}
                        onClick={() => {
                          const note = window.prompt('Why is it rejected? (recorded in the audit chain)', 'Not needed');
                          if (note === null) return;
                          void act('code', () => rejectMaintenanceCodeChange(pr.id, note.trim() || undefined));
                        }}
                      >
                        Reject
                      </Button>
                    )}
                    {canApprove && (
                      <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => void act('code', () => cancelMaintenanceCodeChange(pr.id))}>
                        Cancel the ask
                      </Button>
                    )}
                    {own ? (
                      <span className="text-muted-foreground" data-testid="maintenance-four-eyes-note">
                        Four eyes: you asked for it; a different approver must approve and will see the code.
                      </span>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>
        )}
        {fresh && (
          <div className="rounded-md border border-warning/50 p-3 space-y-2" data-testid="maintenance-code-once">
            <div className="text-xs font-medium">
              The new code. It is shown this once; the console keeps only a hash of it.
            </div>
            <div className="flex items-center gap-2">
              <code className="font-mono text-base tracking-wider">{fresh.code}</code>
              <Button size="sm" variant="glass" onClick={() => void copyCode()} title="Copy the code">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Store it in your organisation&apos;s password vault now, and clear the clipboard if you copied it. To remove
              the agent from a laptop that cannot reach the console, an administrator saves it in a file on that laptop,
              runs the uninstall with FOCTTA_MAINTENANCE_CODE_FILE set to that file, and deletes the file afterwards.
              If the code was typed on a machine you do not control, rotate it. Do not send it by e-mail or chat.
            </div>
            <Button size="sm" variant="glass" onClick={() => setFresh(null)}>
              <X className="h-3.5 w-3.5 mr-1" />
              I have stored it
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-2" data-testid="removal-waiting">
        <div className="text-sm">Waiting for approval ({waiting.length})</div>
        {waiting.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            None. Ask for a removal from a device&apos;s details (Remove agent).
          </div>
        ) : (
          waiting.map((r) => {
            const own = ownRequest(r, actorId);
            return (
              <div key={r.id} className="glass-lite rounded-md p-3 text-xs space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{r.displayName ?? r.podId}</div>
                  <StateBadge r={r} />
                </div>
                <div>Reason: {r.reason}</div>
                <div className="text-muted-foreground">
                  Asked by {r.requestedBy} on {when(r.requestedAt)}; expires {when(r.expiresAt)}.
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {canApprove && (
                    <Button
                      data-testid="approve-removal"
                      size="sm"
                      variant="destructive"
                      disabled={busy !== null || own}
                      title={own ? 'Four eyes: you asked for it; a different approver approves' : undefined}
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Remove the FOCTTA agent from ${r.displayName ?? r.podId}?\n\nAsked by ${r.requestedBy}: ${r.reason}\n\n` +
                              'The agent uninstalls itself at its next check-in, and this laptop is no longer scanned or protected. ' +
                              'Until the device collects it, the removal can still be cancelled here.',
                          )
                        )
                          return;
                        void act(`approve-${r.id}`, () => approveRemoval(r.id));
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Approve removal
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      size="sm"
                      variant="glass"
                      disabled={busy !== null}
                      onClick={() => {
                        const note = window.prompt('Why is it rejected? (recorded in the audit chain)', 'Still needed');
                        if (note === null) return;
                        void act(`reject-${r.id}`, () => rejectRemoval(r.id, note.trim() || undefined));
                      }}
                    >
                      Reject
                    </Button>
                  )}
                  {canAsk && (
                    <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => void act(`cancel-${r.id}`, () => cancelRemoval(r.id))}>
                      Cancel
                    </Button>
                  )}
                  {own && canApprove ? (
                    <span className="text-muted-foreground" data-testid="removal-four-eyes-note">
                      Four eyes: you asked for it; a different approver must approve it.
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      {history.length > 0 && (
        <div className="space-y-1 mt-4" data-testid="removal-history">
          <div className="text-sm">Recent decisions</div>
          {history.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 text-xs border-b border-border/20 py-1">
              <span>
                {r.displayName ?? r.podId}: {r.reason}
                {r.decidedBy ? ` (decided by ${r.decidedBy})` : ''}
                {r.deviceDetail ? `; device: ${r.deviceDetail}` : ''}
              </span>
              <span className="flex items-center gap-2 shrink-0">
                {canAsk && r.state === 'approved' && r.progress === 'waiting_for_device' && (
                  <Button
                    data-testid="cancel-approved-removal"
                    size="sm"
                    variant="glass"
                    disabled={busy !== null}
                    title="The device has not collected it yet: cancelling means it never receives it"
                    onClick={() => void act(`cancel-${r.id}`, () => cancelRemoval(r.id))}
                  >
                    Cancel removal
                  </Button>
                )}
                <StateBadge r={r} />
              </span>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}
